import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, ChevronLeft } from "lucide-react";
import { fileUrl, formatPrice, formatDate } from "@/lib/api";

function ChatHeader({ active, onBack }) {
  const listing = active.listing || {};
  return (
    <div className="border-b-2 border-black p-3 bg-[#F9F9F9] flex items-center gap-3">
      <button data-testid="mobile-back-to-threads" onClick={onBack} className="md:hidden border-2 border-black p-1.5 bg-white" aria-label="Back to conversations"><ChevronLeft size={16} /></button>
      <Link to={`/listing/${listing.listing_id}`} className="flex items-center gap-3 no-underline text-black hover:no-underline min-w-0">
        {listing.images?.[0] && <img src={fileUrl(listing.images[0])} alt="" className="w-12 h-12 object-cover border border-black" />}
        <div>
          <div className="font-mono-data text-xs uppercase font-bold">{active.other?.name || "Unknown"}</div>
          <div className="text-xs link-blue">{listing.title} · {formatPrice(listing.price, listing.currency)}</div>
        </div>
      </Link>
    </div>
  );
}

function MessageBubble({ msg, mine }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] md:max-w-[70%] border-2 border-black p-2 font-mono-data text-sm ${mine ? "bg-[#0000EE] text-white" : "bg-[#F9F9F9] text-black"}`}>
        <div className="whitespace-pre-wrap">{msg.body}</div>
        <div className={`text-[10px] uppercase mt-1 ${mine ? "text-white/70" : "text-gray-500"}`}>{formatDate(msg.created_at)}</div>
      </div>
    </div>
  );
}

function MessageList({ msgs, userId }) {
  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs]);
  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-white">
      {msgs.map((m) => <MessageBubble key={m.message_id} msg={m} mine={m.from_user_id === userId} />)}
    </div>
  );
}

function MessageInput({ onSend }) {
  const [body, setBody] = useState("");
  const submit = async () => {
    if (!body.trim()) return;
    await onSend(body);
    setBody("");
  };
  return (
    <div className="border-t-2 border-black p-3 flex gap-2">
      <input
        data-testid="msg-input"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        placeholder="type a message…"
        className="flex-1 min-w-0 border-2 border-black px-3 py-2 font-mono-data text-base md:text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-[#0000EE]"
      />
      <button data-testid="msg-send-btn" onClick={submit} className="bg-black text-white px-4 py-2 border-2 border-black font-mono-data text-xs uppercase hover:bg-[#0000EE] hover:border-[#0000EE] flex items-center gap-1" aria-label="Send">
        <Send size={14} /> <span className="hidden sm:inline">Send</span>
      </button>
    </div>
  );
}

export function ChatPane({ active, msgs, userId, onSend, onBack, className }) {
  return (
    <section className={className} data-testid="message-pane">
      {!active && <div className="flex-1 flex items-center justify-center font-mono-data text-xs uppercase text-gray-500">Select a conversation</div>}
      {active && (
        <>
          <ChatHeader active={active} onBack={onBack} />
          <MessageList msgs={msgs} userId={userId} />
          <MessageInput onSend={onSend} />
        </>
      )}
    </section>
  );
}
